import React, { useState, useEffect } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import AlertModal from '../../AlertModal';
import { PRICING_PLANS } from '../constants';
import { BillingCycle, Plan, PricingTier } from '../types';
import { createCheckoutSessionSafely, getCurrentSubscription } from '../../../utils/subscriptionApi';
import { useAuth } from '../../../hooks/useAuth';
import { isPrerendering } from '../../../utils/prerender';

const PricingPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>(BillingCycle.Monthly);
  const [loadingPlan, setLoadingPlan] = useState<PricingTier | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<any>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModalConfig, setAlertModalConfig] = useState({
    title: '',
    message: '',
    buttonText: 'OK',
    buttonVariant: 'primary' as 'primary' | 'danger' | 'secondary' | 'success'
  });
  const { user } = useAuth();

  // Fetch current subscription when user is authenticated
  useEffect(() => {
    if (isPrerendering()) {
      // During prerender, skip subscription fetch to avoid network calls
      setSubscriptionLoading(false);
      return;
    }
    const fetchCurrentSubscription = async () => {
      if (!user) {
        setCurrentSubscription(null);
        return;
      }

      setSubscriptionLoading(true);
      try {
        const subscription = await getCurrentSubscription();
        setCurrentSubscription(subscription);
      } catch (error) {
        console.error('Error fetching current subscription:', error);
        setCurrentSubscription(null);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchCurrentSubscription();
  }, [user]);

  // Helper function to show alert modal
  const showAlert = (title: string, message: string, buttonVariant: 'primary' | 'danger' | 'secondary' | 'success' = 'primary') => {
    setAlertModalConfig({
      title,
      message,
      buttonText: 'OK',
      buttonVariant
    });
    setShowAlertModal(true);
  };

  const toggleBillingCycle = () => {
    setBillingCycle((prev: BillingCycle) => prev === BillingCycle.Monthly ? BillingCycle.Annually : BillingCycle.Monthly);
  };

  const isPlanDisabled = (plan: Plan) => {
    // Pro plans are disabled
    if (plan.name === PricingTier.Pro) {
      return true;
    }
    
    // Check if user has active subscription
    if (currentSubscription?.subscription && ['active', 'trialing'].includes(currentSubscription.subscription.status)) {
      return true;
    }
    
    return false;
  };

  const getPlanButtonText = (plan: Plan) => {
    if (plan.name === PricingTier.Pro) {
      return 'Coming Soon';
    }
    
    if (currentSubscription?.subscription && ['active', 'trialing'].includes(currentSubscription.subscription.status)) {
      return `You have ${currentSubscription.subscription.tier} Plan`;
    }
    
    return plan.name === PricingTier.Free ? 'Get Started' : `Choose ${plan.name}`;
  };

  const handleSubscribe = async (plan: Plan) => {
    
    // Check if Pro plan is disabled
    if (plan.name === PricingTier.Pro) {
      showAlert(
        'Pro Plan Coming Soon',
        'Pro plans are coming soon! Contact us for early access pricing. For now, please choose Basic or Free plan.',
        'secondary'
      );
      return;
    }
    
    // Check if user already has active subscription
    if (currentSubscription?.subscription && ['active', 'trialing'].includes(currentSubscription.subscription.status)) {
      showAlert(
        'Active Subscription Found',
        `You already have an active ${currentSubscription.subscription.tier} subscription. Please manage your existing subscription instead.`,
        'secondary'
      );
      return;
    }
    
    if (plan.name === PricingTier.Free) {
      // Handle free plan - redirect to signup or app
      window.location.href = user ? '/app' : '/app';
      return;
    }

    if (!user) {
      // Redirect to login if not authenticated with plan information in URL
      const params = new URLSearchParams({
        plan: plan.name.toLowerCase(),
        billing: billingCycle.toLowerCase(),
        origin: 'pricing'
      });
      window.location.href = `/app?${params.toString()}`;
      return;
    }

    try {
      setLoadingPlan(plan.name);
      await createCheckoutSessionSafely(plan.name, billingCycle);
    } catch (error) {
      console.error('Error creating checkout session:', error);
      
      // Check if this is an "already have subscription" error
      if (error instanceof Error && error.message.includes('already have an active')) {
        // Show a friendly message and redirect to app
        showAlert(
          'Active Subscription Found',
          'You already have an active subscription! Redirecting you to ThreadIt...',
          'success'
        );
        setTimeout(() => {
          window.location.href = '/app';
        }, 3000); // Increased to 3 seconds for better readability
      } else {
        // Show generic error for other issues
        showAlert(
          'Checkout Error',
          error instanceof Error ? error.message : 'Failed to start checkout. Please try again.',
          'danger'
        );
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900">Find the Perfect Plan</h1>
          <p className="mt-4 text-lg text-slate-600">Start for free, then upgrade to unlock powerful AI features.</p>
        </div>

        <div className="flex justify-center items-center space-x-4 mb-12">
          <span className={`font-medium ${billingCycle === BillingCycle.Monthly ? 'text-slate-800' : 'text-slate-500'}`}>
            Monthly
          </span>
          <label htmlFor="billing-toggle" className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" id="billing-toggle" className="sr-only peer" checked={billingCycle === BillingCycle.Annually} onChange={toggleBillingCycle} />
            <div className="w-14 h-8 bg-slate-300 rounded-full peer-checked:bg-emerald-500 transition-colors peer-focus:ring-4 peer-focus:ring-emerald-500/30"></div>
            <span className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform peer-checked:translate-x-6"></span>
          </label>
          <span className={`font-medium ${billingCycle === BillingCycle.Annually ? 'text-slate-800' : 'text-slate-500'}`}>
            Annually <span className="text-emerald-500">(Save up to 20%)</span>
          </span>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 max-w-5xl mx-auto items-start">
          {PRICING_PLANS.map((plan: Plan) => {
            const isDisabled = isPlanDisabled(plan);
            const buttonText = getPlanButtonText(plan);
            
            return (
              <Card key={plan.name} className={`flex flex-col h-full relative ${
                plan.isFeatured ? 'border-emerald-500 scale-105' : 
                plan.name === PricingTier.Pro ? 'border-purple-500 scale-105 bg-gradient-to-br from-purple-50 to-indigo-50 shadow-xl' : 
                'border-white/50'
              } ${isDisabled ? 'opacity-60' : ''} ${
                plan.name === PricingTier.Pro ? 'backdrop-blur-sm' : ''
              }`}>
                {plan.isFeatured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                    Most Popular
                  </div>
                )}
                {plan.name === PricingTier.Pro && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg">
                    Coming Soon
                  </div>
                )}
                {currentSubscription?.subscription && ['active', 'trialing'].includes(currentSubscription.subscription.status) && plan.name !== PricingTier.Free && plan.name !== PricingTier.Pro && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                    Current Plan
                  </div>
                )}
                <div className="flex-grow">
                  <h3 className={`text-2xl font-semibold mb-2 ${
                    plan.name === PricingTier.Pro ? 'text-purple-800' : 
                    isDisabled ? 'text-slate-500' : 'text-slate-800'
                  }`}>{plan.name}</h3>
                  <p className={`mb-6 min-h-[40px] ${
                    plan.name === PricingTier.Pro ? 'text-purple-600' : 
                    isDisabled ? 'text-slate-400' : 'text-slate-600'
                  }`}>{plan.description}</p>
                  <div className="mb-6">
                    {plan.name === PricingTier.Pro ? (
                      <div className="text-center">
                        <a 
                          href="/contact" 
                          className="text-2xl font-bold text-purple-900 hover:text-purple-700 transition-colors cursor-pointer underline decoration-purple-300 hover:decoration-purple-500"
                        >
                          Contact Us
                        </a>
                        <p className="text-sm text-purple-600 mt-1">For early access pricing</p>
                      </div>
                    ) : (
                      <>
                        <span className={`text-4xl font-bold ${
                          isDisabled ? 'text-slate-400' : 'text-slate-900'
                        }`}>{plan.price[billingCycle]}</span>
                        {plan.price[billingCycle] !== '$0' && (
                           <span className={`${
                             isDisabled ? 'text-slate-400' : 'text-slate-500'
                           }`}>/ month</span>
                        )}
                      </>
                    )}
                  </div>
                  <ul className="space-y-4">
                    {plan.features.map((feature: string, index: number) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 flex-shrink-0 mt-1 ${
                          feature.includes('coming soon') ? 'text-purple-500' :
                          plan.name === PricingTier.Pro ? 'text-purple-500' :
                          isDisabled ? 'text-slate-400' : 'text-emerald-500'
                        }`} />
                        <span className={`${
                          feature.includes('coming soon') ? 'text-purple-600 font-medium' :
                          plan.name === PricingTier.Pro ? 'text-purple-700' :
                          isDisabled ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                          {plan.name === PricingTier.Pro && feature === 'Deep thinking models' ? (
                            <span className="inline-flex items-center rounded-full border border-purple-300/70 bg-purple-50/80 text-purple-800 px-3 py-1 text-xs font-medium shadow-sm">
                              Deep thinking models
                            </span>
                          ) : (
                            feature
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-8">
                  <Button 
                    className={`w-full ${
                      plan.name === PricingTier.Pro ? 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white border-0' : ''
                    }`}
                    variant={plan.isFeatured ? 'primary' : 'secondary'}
                    onClick={() => handleSubscribe(plan)}
                    disabled={loadingPlan === plan.name || isDisabled || subscriptionLoading}
                  >
                    {loadingPlan === plan.name ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span>Processing...</span>
                      </div>
                    ) : subscriptionLoading ? (
                      <div className="flex items-center justify-center">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        <span>Loading...</span>
                      </div>
                    ) : (
                      buttonText
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Alert Modal */}
      {showAlertModal && (
        <AlertModal
          isOpen={showAlertModal}
          title={alertModalConfig.title}
          message={alertModalConfig.message}
          buttonText={alertModalConfig.buttonText}
          buttonVariant={alertModalConfig.buttonVariant}
          onClose={() => setShowAlertModal(false)}
        />
      )}
    </div>
  );
};

export default PricingPage;